import { mkdir } from 'node:fs/promises';
import { join } from 'node:path';
import { match } from 'variant';
import type { TFunction } from '@wix/cli-i18n';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { gitCommit, gitInit, runCommand } from '@wix/package-manager';
import type { IPackageManager, RepoType } from '@wix/package-manager';
import type { AppData } from './types.js';
import type { TemplateSource } from './parse-command-options.js';
import type { Task } from './task-runner.js';
import type { TemplateParams } from './validations/index.js';
import type { McpConfigResult } from './components/mcp/getMcpResult.js';

export interface TaskData {
  appData: AppData;
  packageManager: IPackageManager;
  repoType: RepoType;
  packageName: string;
  template: TemplateSource;
  templateParams?: TemplateParams;
  targetParentFolder: string;
  skipInstall: boolean;
  skipGit: boolean;
  mcpConfigResult: McpConfigResult;
}

export function getTasks(
  {
    appData,
    packageManager,
    repoType,
    packageName,
    template,
    templateParams,
    targetParentFolder,
    skipInstall,
    skipGit,
    mcpConfigResult,
  }: TaskData,
  t: TFunction
) {
  const packageFolder = join(targetParentFolder, packageName);

  const generateProject = async () => {
    try {
      await mkdir(packageFolder, { recursive: true });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCreateProjectFolder({ packageFolder }),
        cause: e,
      });
    }

    const { generateApp } = await import('./services/generator/index.js');

    await generateApp({
      packageName,
      packageFolder,
      appData,
      packageManager,
      repoType,
      template,
      templateParams,
      ideForMcp: match(mcpConfigResult, {
        Created: ({ ideForMcp }) => ideForMcp,
        Canceled: () => {},
      }),
    });
    await packageManager.setup(packageFolder);
  };

  const initializeGit = async () => {
    try {
      await gitInit(packageFolder);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToInitializeGitRepo(),
        cause: e,
      });
    }
  };

  const installDependencies = async () => {
    try {
      await packageManager.runInstall(packageFolder);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToInstallDependencies(),
        cause: e,
      });
    }
  };

  const addWixSkills = async () => {
    const args = [
      '--yes',
      'skills',
      'add',
      'wix/skills',
      // The `skills` CLI uses -y/--yes to skip prompts.
      '-y',
    ] as const;

    /**
     * Test-only escape hatch:
     * In tests we want deterministic interception of `npx` without relying
     * on PATH ordering, so we allow overriding the executable via env var.
     */
    const npxExecutable = process.env.WIX_CLI_NPX_PATH ?? 'npx';

    try {
      await runCommand(`${npxExecutable} ${args.join(' ')}`, {
        cwd: packageFolder,
      });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToRunExternalCommand({
          file: 'npx',
          args: [...args],
        }),
        cause: e,
      });
    }
  };

  const commitToGit = async () => {
    try {
      await gitCommit(packageFolder);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCommitChanges(),
        cause: e,
      });
    }
  };

  const generateProjectTask: Task = {
    action: generateProject,
    loadingText: t('create_app.generate_project.generating_files.loading'),
    successText: t('create_app.generate_project.generating_files.done'),
  };

  const initializeGitTask: Task = {
    action: initializeGit,
    loadingText: t('create_app.generate_project.init_git.loading'),
    successText: t('create_app.generate_project.init_git.done'),
  };

  const installDependenciesTask: Task = {
    action: installDependencies,
    loadingText: t(
      'create_app.generate_project.installing_dependencies.loading',
      {
        command: packageManager.getInstallCmd(),
      }
    ),
    successText: t('create_app.generate_project.installing_dependencies.done'),
  };

  const addWixSkillsTask: Task = {
    action: addWixSkills,
    loadingText: t('create_app.generate_project.adding_skills.loading'),
    successText: t('create_app.generate_project.adding_skills.done'),
  };

  const commitToGitTask: Task = {
    action: commitToGit,
    loadingText: t('create_app.generate_project.git_commit.loading'),
    successText: t('create_app.generate_project.git_commit.done'),
  };

  const optionalInstallTask = skipInstall
    ? []
    : [installDependenciesTask, addWixSkillsTask];

  return match(repoType, {
    None: () => [
      generateProjectTask,
      ...(skipGit ? [] : [initializeGitTask]),
      ...optionalInstallTask,
      ...(skipGit ? [] : [commitToGitTask]),
    ],
    Polyrepo: () => [generateProjectTask, ...optionalInstallTask],
    Monorepo: () => [generateProjectTask, ...optionalInstallTask],
  });
}
